import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { uploadsAbsoluteUrl } from '../utils/uploadsBase';

const ContentContext = createContext();

export const useContent = () => {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('useContent должен использоваться внутри ContentProvider');
  }
  return context;
};

function getNestedValue(obj, path, fallback) {
  const pickFallback = () =>
    fallback !== undefined && fallback !== null ? fallback : path;

  if (!obj || typeof obj !== 'object') {
    return pickFallback();
  }
  const keys = path.split('.');
  let current = obj;
  for (const key of keys) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return pickFallback();
    }
    current = current[key];
  }
  if (current === null || current === undefined || current === '') {
    return pickFallback();
  }
  return current;
}

function parseContent(raw) {
  if (raw == null) return {};
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }
  return raw;
}

function locContent(raw, lang) {
  const c = parseContent(raw);
  return c[lang] || c.en || {};
}

function mapBenefitsToUi(benefits) {
  const b = Array.isArray(benefits) ? benefits : [];
  return {
    benefit1: { title: b[0]?.title ?? '', text: b[0]?.text ?? '' },
    benefit2: { title: b[1]?.title ?? '', text: b[1]?.text ?? '' },
    benefit3: { title: b[2]?.title ?? '', text: b[2]?.text ?? '' },
  };
}

/**
 * Данные главной для t('home.hero.title'), t('home.main.title'), …
 */
export function buildHomePageData(components, lang, apiUrl) {
  const comps = components || [];
  const u = (path) => (path ? uploadsAbsoluteUrl(path, apiUrl) : '');
  const out = {};

  const hero = comps.find((c) => c.type === 'HERO');
  if (hero) {
    const L = locContent(hero.content, lang);
    const btns = Array.isArray(L.buttons) ? L.buttons : [];
    out.hero = {
      title: L.heading ?? '',
      subtitle: L.subheading ?? '',
      journalText: btns[0]?.label ?? '',
      journalLink: btns[0]?.href ?? '',
      conferenceText: btns[1]?.label ?? '',
      conferenceLink: btns[1]?.href ?? '',
      backgroundImage: hero.media?.url ? u(hero.media.url) : '',
    };
  }

  const main = comps.find((c) => c.type === 'MAIN');
  if (main) {
    const L = locContent(main.content, lang);
    out.main = {
      title: L.title ?? '',
      description: L.description ?? '',
      button: { text: L.buttonText ?? '', link: L.buttonLink ?? '' },
      image: main.media?.url ? u(main.media.url) : '',
      imageAlt: L.imageAlt ?? '',
    };
  }

  const about = comps.find((c) => c.type === 'ABOUT');
  if (about) {
    const L = locContent(about.content, lang);
    out.about = {
      title: L.title ?? '',
      description: L.description ?? '',
      button: { text: L.buttonText ?? '', link: L.buttonLink ?? '' },
    };
  }

  const inv = comps.find((c) => c.type === 'INVITATION');
  if (inv) {
    const L = locContent(inv.content, lang);
    out.invitation = {
      title: L.title ?? '',
      subtitle: L.subtitle ?? '',
      description: L.description ?? '',
      image: inv.media?.url ? u(inv.media.url) : '',
      imageAlt: L.imageAlt ?? '',
    };
  }

  const part = comps.find((c) => c.type === 'PARTICIPATION');
  if (part) {
    const L = locContent(part.content, lang);
    const ip = L.inperson || {};
    const rm = L.remote || {};
    out.participation = {
      title: L.title ?? '',
      inperson: {
        title: ip.title ?? '',
        subtitle: ip.subtitle ?? '',
        ...mapBenefitsToUi(ip.benefits),
      },
      remote: {
        title: rm.title ?? '',
        subtitle: rm.subtitle ?? '',
        ...mapBenefitsToUi(rm.benefits),
      },
    };
  }

  const idx = comps.find((c) => c.type === 'INDEXING');
  if (idx) {
    const L = locContent(idx.content, lang);
    out.indexing = {
      title: L.title ?? '',
      description: L.description ?? '',
    };
  }

  const dates = comps.find((c) => c.type === 'DATES');
  if (dates) {
    const L = locContent(dates.content, lang);
    // backward compat: старый формат { title, items }
    if (Array.isArray(L.items)) {
      out.dates = {
        inperson: {
          title: L.title ?? '',
          items: L.items,
        },
        remote: {
          title: '',
          items: [],
        },
      };
    } else {
      out.dates = {
        inperson: {
          title: L.inperson?.title ?? '',
          items: Array.isArray(L.inperson?.items) ? L.inperson.items : [],
        },
        remote: {
          title: L.remote?.title ?? '',
          items: Array.isArray(L.remote?.items) ? L.remote.items : [],
        },
      };
    }
  }

  const topics = comps.find((c) => c.type === 'TOPICS');
  if (topics) {
    const L = locContent(topics.content, lang);
    // backward compat: старый формат { title, items:[{label,description}] }
    if (Array.isArray(L.items)) {
      out.topics = {
        title: L.title ?? '',
        groups: [
          {
            title1: '',
            title2: '',
            items: L.items.map((x) =>
              [x?.label, x?.description].filter(Boolean).join(' — '),
            ),
          },
        ],
      };
    } else {
      out.topics = {
        title: L.title ?? '',
        groups: Array.isArray(L.groups) ? L.groups : [],
      };
    }
  }

  return out;
}

export const ContentProvider = ({
  children,
  defaultLanguage = 'en',
  apiUrl = 'http://localhost:5000/api',
}) => {
  const [content, setContent] = useState({});
  const [currentLanguage, setCurrentLanguage] = useState(defaultLanguage);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const transformContent = (pages, lang) => {
    const result = {};
    pages.forEach((page) => {
      const systemKey = page.systemKey || 'page';
      if (!result[systemKey]) result[systemKey] = {};

      if (systemKey === 'home') {
        const homeData = buildHomePageData(page.components || [], lang, apiUrl);
        Object.assign(result.home, homeData);
        return;
      }

      // About-us: CONTENT хранит вложенную структуру hero/aspects/partner/objectives,
      // поэтому разворачиваем её прямо в result.about, чтобы t('about.hero.title') и т.п. работали.
      if (systemKey === 'about') {
        page.components?.forEach((comp) => {
          const compContent = parseContent(comp.content);
          const localizedRoot =
            (compContent && compContent[lang]) ||
            (compContent && compContent.en) ||
            {};
          Object.assign(result.about, localizedRoot);
        });
        return;
      }

      if (systemKey === 'news') {
        page.components?.forEach((comp) => {
          const compContent = parseContent(comp.content);
          const localizedRoot =
            (compContent && compContent[lang]) ||
            (compContent && compContent.en) ||
            {};
          Object.assign(result.news, localizedRoot);
        });
        return;
      }

      if (systemKey === 'contact') {
        page.components?.forEach((comp) => {
          const compContent = parseContent(comp.content);
          const localizedRoot =
            (compContent && compContent[lang]) ||
            (compContent && compContent.en) ||
            {};
          Object.assign(result.contact, localizedRoot);
        });
        return;
      }

      if (systemKey === 'aboutConference') {
        page.components?.forEach((comp) => {
          const compContent = parseContent(comp.content);
          const localizedRoot =
            (compContent && compContent[lang]) ||
            (compContent && compContent.en) ||
            {};
          Object.assign(result.aboutConference, localizedRoot);
        });
        return;
      }

      if (systemKey === 'aboutJournal') {
        page.components?.forEach((comp) => {
          const compContent = parseContent(comp.content);
          const localizedRoot =
            (compContent && compContent[lang]) ||
            (compContent && compContent.en) ||
            {};
          Object.assign(result.aboutJournal, localizedRoot);
        });
        return;
      }

      if (systemKey === 'guidelinesReviewers') {
        page.components?.forEach((comp) => {
          const compContent = parseContent(comp.content);
          const localizedRoot =
            (compContent && compContent[lang]) ||
            (compContent && compContent.en) ||
            {};
          Object.assign(result.guidelinesReviewers, localizedRoot);
        });
        return;
      }

      if (systemKey === 'guidelinesAuthors') {
        page.components?.forEach((comp) => {
          const compContent = parseContent(comp.content);
          const localizedRoot =
            (compContent && compContent[lang]) ||
            (compContent && compContent.en) ||
            {};
          Object.assign(result.guidelinesAuthors, localizedRoot);
        });
        return;
      }

      if (systemKey === 'ethics') {
        page.components?.forEach((comp) => {
          const compContent = parseContent(comp.content);
          const localizedRoot =
            (compContent && compContent[lang]) ||
            (compContent && compContent.en) ||
            {};
          Object.assign(result.ethics, localizedRoot);
        });
        return;
      }

      if (systemKey === 'editorialBoard') {
        page.components?.forEach((comp) => {
          const compContent = parseContent(comp.content);
          const localizedRoot =
            (compContent && compContent[lang]) ||
            (compContent && compContent.en) ||
            {};
          Object.assign(result.editorialBoard, localizedRoot);
        });
        return;
      }

      if (systemKey === 'conferenceMain') {
        page.components?.forEach((comp) => {
          const compContent = parseContent(comp.content);
          const localizedRoot =
            (compContent && compContent[lang]) ||
            (compContent && compContent.en) ||
            {};
          Object.assign(result.conferenceMain, localizedRoot);
        });
        return;
      }

      if (systemKey === 'conferencePastNew') {
        page.components?.forEach((comp) => {
          const compContent = parseContent(comp.content);
          const localizedRoot =
            (compContent && compContent[lang]) ||
            (compContent && compContent.en) ||
            {};
          Object.assign(result.conferencePastNew, localizedRoot);
        });
        return;
      }

      if (systemKey === 'conferenceRegister') {
        page.components?.forEach((comp) => {
          const compContent = parseContent(comp.content);
          const localizedRoot =
            (compContent && compContent[lang]) ||
            (compContent && compContent.en) ||
            {};
          Object.assign(result.conferenceRegister, localizedRoot);
        });
        return;
      }

      if (systemKey === 'conferenceAccommodation') {
        page.components?.forEach((comp) => {
          const compContent = parseContent(comp.content);
          const localizedRoot =
            (compContent && compContent[lang]) ||
            (compContent && compContent.en) ||
            {};
          Object.assign(result.conferenceAccommodation, localizedRoot);
        });
        return;
      }

      if (systemKey === 'articleSubmission') {
        page.components?.forEach((comp) => {
          const compContent = parseContent(comp.content);
          const localizedRoot =
            (compContent && compContent[lang]) ||
            (compContent && compContent.en) ||
            {};
          Object.assign(result.articleSubmission, localizedRoot);
        });
        return;
      }

      if (systemKey === 'articleSubmit') {
        page.components?.forEach((comp) => {
          const compContent = parseContent(comp.content);
          const localizedRoot =
            (compContent && compContent[lang]) ||
            (compContent && compContent.en) ||
            {};
          Object.assign(result.articleSubmit, localizedRoot);
        });
        return;
      }

      page.components?.forEach((comp) => {
        const compContent = parseContent(comp.content);
        const localizedContent = {};
        Object.keys(compContent).forEach((key) => {
          const value = compContent[key];
          if (
            value &&
            typeof value === 'object' &&
            !Array.isArray(value) &&
            value[lang] !== undefined
          ) {
            localizedContent[key] = value[lang];
          } else if (
            value &&
            typeof value === 'object' &&
            !Array.isArray(value) &&
            value.en !== undefined
          ) {
            localizedContent[key] = value.en;
          } else {
            localizedContent[key] = value;
          }
        });
        if (!result[systemKey][comp.type]) {
          result[systemKey][comp.type] = localizedContent;
        }
      });
    });
    return result;
  };

  const loadContent = async (lang) => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${apiUrl}/content/pages`);
      const pages = response.data.pages || [];
      setContent(transformContent(pages, lang));
    } catch (err) {
      console.error('Ошибка загрузки контента:', err);
      setError(err.message);
      setContent({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContent(currentLanguage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLanguage]);

  const t = (key, fallback) => {
    if (!key || typeof key !== 'string') {
      return fallback !== undefined && fallback !== null ? fallback : '';
    }
    return getNestedValue(content, key, fallback);
  };

  const changeLanguage = (lang) => {
    if (lang !== currentLanguage) {
      setCurrentLanguage(lang);
    }
  };

  const value = {
    content,
    currentLanguage,
    loading,
    error,
    t,
    changeLanguage,
    reload: () => loadContent(currentLanguage),
  };

  return (
    <ContentContext.Provider value={value}>
      {children}
    </ContentContext.Provider>
  );
};
